import { EVENTS } from '@/lib/consts.ts'
import {
  getDateText,
  getMaximumTravelDistance,
  getRegistrationMethod,
  simplifyCategory,
  getCategories,
  getBusinessMarketingScore,
} from '@/lib/analytics/helpers.js'

import { path } from 'ramda'

import {
  BUSINESS_REGISTRATION_STARTED_EVENTS,
  BUSINESS_REGISTRATION_COMPLETED_EVENTS,
  PRICE_TYPE,
  USE_IDENTIFY,
  DEFAULT_CURRENCY,
} from '@/lib/analytics/consts.ts'

import {
  useOnboardingPolls,
} from '@/components/onboarding/composables/polls/index.ts'

export const segment1Onboarding = {
  name: 'segment1Onboarding',

  [EVENTS.ONBOARDING_NEW_EMAIL_ENTERED] ({ data }) {
    const loginType = path(['loginType'], data)
    const registration_method = getRegistrationMethod(loginType)
    const onboarding_start_date = getDateText(new Date())

    return {
      event: 'Business_Registration_Started_With_Email',
      event_name: EVENTS.ONBOARDING_NEW_EMAIL_ENTERED,
      [USE_IDENTIFY]: true,
      onboarding_start_date,
      registration_method,
    }
  },

  [EVENTS.ONBOARDING_AGREEMENTS_UPDATED] ({ data }) {
    const items = path(['items'], data) || []
    const marketing_agreement = items.find(item => item.name === 'marketing_agreement')
    const business_marketing_agreement = path(['value'], marketing_agreement)

    return {
      event: 'Business_Contact_Preferences_Updated',
      event_name: EVENTS.ONBOARDING_AGREEMENTS_UPDATED,
      business_marketing_agreement,
      [USE_IDENTIFY]: true,
    }
  },

  [EVENTS.ONBOARDING_BUSINESS_NAME_SET] ({ data }) {
    const business = path(['business'], data) || {}
    const business_name = path(['name'], business)

    return {
      event: 'Onboarding_Business_Name_Set',
      event_name: EVENTS.ONBOARDING_BUSINESS_NAME_SET,
      business_name,
      [USE_IDENTIFY]: true,
    }
  },

  [EVENTS.ONBOARDING_OWNER_NAME_SET] ({ data }) {
    const business = path(['business'], data) || {}
    const owner_name = path(['owner_full_name'], business)

    return {
      event: 'Onboarding_Owner_Name_Set',
      event_name: EVENTS.ONBOARDING_OWNER_NAME_SET,
      owner_name,
      [USE_IDENTIFY]: true,
    }
  },

  [EVENTS.ONBOARDING_BUSINESS_PHONE_SET] () {
    return {
      event: 'Onboarding_Business_Phone_Set',
      event_name: EVENTS.ONBOARDING_BUSINESS_PHONE_SET,
      [USE_IDENTIFY]: true,
    }
  },

  [EVENTS.ONBOARDING_PASSWORD_SET] () {
    return {
      event: 'Onboarding_Password_Set',
      event_name: EVENTS.ONBOARDING_PASSWORD_SET,
      [USE_IDENTIFY]: true,
    }
  },

  [EVENTS.ONBOARDING_B_LISTING_CLAIMED] () {
    return {
      event: 'Onboarding_Blisting_Claim',
      event_name: EVENTS.ONBOARDING_B_LISTING_CLAIMED,
      [USE_IDENTIFY]: true,
    }
  },

  [EVENTS.ONBOARDING_BUSINESS_REGISTRATION_CATEGORY_SELECTED] (data) {
    const businessPrimaryCategory = simplifyCategory(path(['primaryCategory'], data))
    const simplifiedCategories = getCategories(path(['categories'], data))

    const payload = {
      business_primary_category: businessPrimaryCategory,
      categories: simplifiedCategories,
      settings: this.getFacebookSettings(),
    }
    const events = [{
      event: 'Business_Registration_Category_Selected',
      event_name: EVENTS.ONBOARDING_BUSINESS_REGISTRATION_CATEGORY_SELECTED,
      [USE_IDENTIFY]: true,
      ...payload,
    }]
    const categoryEventName = BUSINESS_REGISTRATION_STARTED_EVENTS[businessPrimaryCategory]

    if (categoryEventName) {
      events.push({
        event: categoryEventName,
        ...payload,
      })
    }

    return events
  },

  [EVENTS.ONBOARDING_WORKPLACE] ({ data, business }) {
    const business_name = business?.name
    const traveling_to_client = data?.type

    return {
      business_name,
      event: 'Onboarding_Workplace',
      event_name: EVENTS.ONBOARDING_WORKPLACE,
      traveling_to_client,
      [USE_IDENTIFY]: true,
    }
  },

  [EVENTS.ONBOARDING_ADDRESS_SEARCH] ({ data }) {
    const business = path(['business'], data) || {}
    const location = path(['location'], business) || {}
    const rentingVenue = path(['renting_venue'], business)
    const is_shared_location = !!rentingVenue
    const name_of_shared_location = path(['name'], rentingVenue)

    return {
      business_name: business.name,
      city: location.city,
      event: 'Onboarding_Address_Search',
      event_name: EVENTS.ONBOARDING_ADDRESS_SEARCH,
      is_shared_location,
      name_of_shared_location,
      owner_email: business.owner_email,
      owner_name: business.owner_full_name,
      // postalCode is named with camel case convencion, because Segment recognizes this as special value
      postalCode: location.zipcode,
      shared_location_room_number: location.address3,
      street_address_and_number: location.address,
      [USE_IDENTIFY]: true,
    }
  },

  [EVENTS.ONBOARDING_ADDRESS] ({ data }) {
    const business = path(['business'], data) || {}
    const location = path(['location'], business) || {}
    const rentingVenue = path(['renting_venue'], business)
    const is_shared_location = !!rentingVenue
    const name_of_shared_location = path(['name'], rentingVenue)
    const { screen_version, is_address_adjusted } = data

    return {
      business_name: business.name,
      city: location.city,
      event: 'Onboarding_Address',
      event_name: EVENTS.ONBOARDING_ADDRESS,
      is_shared_location,
      name_of_shared_location,
      owner_email: business.owner_email,
      owner_name: business.owner_full_name,
      // postalCode is named with camel case convencion, because Segment recognizes this as special value
      postalCode: location.zipcode,
      shared_location_room_number: location.address3,
      street_address_and_number: location.address,
      [USE_IDENTIFY]: true,
      screen_version,
      is_address_adjusted,
    }
  },

  [EVENTS.ONBOARDING_MAP_LOCATION] ({ data }) {
    const business = path(['business'], data) || {}
    const location = path(['location'], business) || {}
    const latitude = path(['coordinate', 'latitude'], location)
    const longitude = path(['coordinate', 'longitude'], location)
    const { entry_source } = data

    return {
      business_name: business.name,
      city: location.city,
      event: 'Onboarding_Map_Location',
      event_name: EVENTS.ONBOARDING_MAP_LOCATION,
      latitude,
      longitude,
      owner_email: business.owner_email,
      owner_name: business.owner_full_name,
      entry_source,
      [USE_IDENTIFY]: true,
    }
  },

  [EVENTS.ONBOARDING_TRAVEL_FEE] ({ data }) {
    const business = path(['business'], data) || {}
    const traveling = business.traveling || {}
    const maximum_travel_distance = getMaximumTravelDistance(traveling)
    const travel_fee = parseFloat(traveling.price) || 0

    return {
      business_name: business.name,
      event: 'Onboarding_Travel_Fee',
      event_name: EVENTS.ONBOARDING_TRAVEL_FEE,
      maximum_travel_distance,
      owner_email: business.owner_email,
      owner_name: business.owner_full_name,
      price_type: PRICE_TYPE[traveling.price_type],
      travel_fee,
      [USE_IDENTIFY]: true,
    }
  },

  [EVENTS.ONBOARDING_BUSINESS_HOURS] ({ data }) {
    const business = path(['business'], data) || {}

    return {
      business_name: business.name,
      event: 'Onboarding_Business_Hours',
      event_name: EVENTS.ONBOARDING_BUSINESS_HOURS,
      owner_email: business.owner_email,
      owner_name: business.owner_full_name,
      [USE_IDENTIFY]: true,
    }
  },

  [EVENTS.ONBOARDING_SERVICE] ({ data }) {
    const business = path(['business'], data) || {}

    return {
      business_name: business.name,
      event: 'Onboarding_Service',
      event_name: EVENTS.ONBOARDING_SERVICE,
      owner_email: business.owner_email,
      owner_name: business.owner_full_name,
      [USE_IDENTIFY]: true,
    }
  },

  [EVENTS.ONBOARDING_BUSINESS_REGISTRATION_COMPLETED] (data) {
    const businessPrimaryCategory = simplifyCategory(path(['primaryCategory'], data))
    const location = path(['business', 'location'], data) || {}
    const simplifiedCategories = getCategories(path(['categories'], data))

    const { getPoll, REGISTRATION_POLLS } = useOnboardingPolls()
    const number_of_staffers = getPoll(REGISTRATION_POLLS.NUMBER_OF_STAFFERS)?.savedInternalNamesChoices || null
    const other_app = getPoll(REGISTRATION_POLLS.OTHER_APPS)?.savedInternalNamesChoices || null

    const payload = {
      business_primary_category: businessPrimaryCategory,
      categories: simplifiedCategories,
      city: location.city,
      // postalCode is named with camel case convencion, because Segment recognizes this as special value
      postalCode: location.zipcode,
      settings: this.getFacebookSettings(),
      number_of_staffers,
      other_app,
      currency: DEFAULT_CURRENCY,
      value: getBusinessMarketingScore(businessPrimaryCategory),
    }
    const events = [
      {
        event: 'Business_Registration_Completed',
        event_name: EVENTS.ONBOARDING_BUSINESS_REGISTRATION_COMPLETED,
        [USE_IDENTIFY]: true,
        ...payload,
      },
    ]

    const categoryEventName = BUSINESS_REGISTRATION_COMPLETED_EVENTS[businessPrimaryCategory]

    if (categoryEventName) {
      events.push({
        event: categoryEventName,
        ...payload,
      })
    }

    return events
  },
}
